/**
 * Sequence Wins Strategy - Higher sequence number wins.
 *
 * Rationale:
 *   Sequence numbers are monotonically incremented on every local save
 *   and persisted to the SyncLedger. Higher sequence = more recent save.
 *   This is the default strategy: deterministic, auditable, no ambiguity.
 *
 * Edge case: equal sequences with different content (device fork, clock skew)
 *   This is an ARCHITECTURAL ANOMALY. Two snapshots should never carry the
 *   same sequence number if the SyncLedger is functioning correctly, because:
 *     - Sequence numbers are minted by SyncLedger.nextSequence() and
 *       immediately persisted to localStorage before the save completes.
 *     - If two snapshots share the same sequence, it means one of:
 *         (a) A SyncLedger write failed silently (hardware/quota error)
 *         (b) Multiple tabs wrote simultaneously (multi-tab conflict)
 *         (c) Sequence was restored from a stale backup
 *
 *   Behaviour on anomaly:
 *     - Local wins (preserves current session work - conservative resolution)
 *     - WARNING logged to system logger
 *     - Anomaly count incremented in module-level counter (observable)
 *     - persistence:reconciliation_needed event emitted (observable via eventBus)
 *
 *   The anomaly is NOT silently normalized. It is explicitly surfaced.
 */

import type { ReconciliationStrategy, ReconciliationContext, ReconciliationResult } from "./reconciliationStrategy";
import { registerStrategy } from "./reconciliationStrategy";
import { logger } from "@/system/logger";
import { eventBus } from "@/runtime/eventBus";

/* =========================================================
   ANOMALY COUNTER
   Observable for diagnostics - resets on page reload.
   ========================================================= */

let _sequenceTieAnomalyCount = 0;

/** Returns the number of equal-sequence + divergent-payload anomalies observed. */
export function getSequenceTieAnomalyCount(): number {
  return _sequenceTieAnomalyCount;
}

/* =========================================================
   STRATEGY
   ========================================================= */

class SequenceWinsStrategyImpl implements ReconciliationStrategy {
  readonly id = "sequence-wins";

  describe(): string {
    return "Higher sequence number wins. Equal sequences resolve to local (current work preserved). Anomalies are logged.";
  }

  resolve(ctx: ReconciliationContext): ReconciliationResult {
    const { local, remote, projectId } = ctx;

    if (local.sequenceNum > remote.sequenceNum) {
      return {
        winner:  "local",
        payload: local,
        reason:  `Local sequence ${local.sequenceNum} > remote ${remote.sequenceNum}`,
      };
    }

    if (remote.sequenceNum > local.sequenceNum) {
      return {
        winner:  "remote",
        payload: remote,
        reason:  `Remote sequence ${remote.sequenceNum} > local ${local.sequenceNum}`,
      };
    }

    // ── Equal sequences ─────────────────────────────────────────────────────────
    // Detect divergent content: if payloads differ at the same sequence number,
    // this is an architectural anomaly - not a normal conflict resolution path.
    const localJson  = JSON.stringify(local);
    const remoteJson = JSON.stringify(remote);
    const isDivergent = localJson !== remoteJson;

    if (isDivergent) {
      _sequenceTieAnomalyCount += 1;

      logger.warn(
        "ReconciliationAnomaly: equal sequence numbers with divergent payloads - " +
        "SyncLedger sequence ownership may be compromised",
        {
          projectId,
          sequenceNum:  local.sequenceNum,
          anomalyCount: _sequenceTieAnomalyCount,
          localSavedAt: local.savedAt,
          remoteSavedAt: remote.savedAt,
          resolution:   "local-wins (conservative)",
        }
      );

      // Emit observable event for diagnostics panel and monitoring
      eventBus.emit({
        type:        "persistence:reconciliation_needed",
        projectId,
        localSeq:    local.sequenceNum,
        remoteSeq:   remote.sequenceNum,
      });
    }

    // Equal sequences - local wins (preserve current session work)
    return {
      winner:  "local",
      payload: local,
      reason:  isDivergent
        ? `ANOMALY: Sequences equal (${local.sequenceNum}) with divergent content - local preserved, anomaly #${_sequenceTieAnomalyCount} logged`
        : `Sequences equal (${local.sequenceNum}) with identical content - local preserved as tiebreaker`,
    };
  }
}

export const sequenceWinsStrategy = new SequenceWinsStrategyImpl();
registerStrategy(sequenceWinsStrategy);
